import { Base } from '@studiometa/js-toolkit';
import { nextFrame, transition } from '@studiometa/js-toolkit/utils';
import MenuItem from './MenuItem.js';
import SubMenuItem from './SubMenuItem.js';

/**
 * @typedef {object} MenuRefs
 * @property {HTMLButtonElement} openMenu
 * @property {HTMLElement} media
 * @property {HTMLElement} menu
 */
/**
 * @typedef {object} MenuInterface
 * @property {MenuRefs} $refs
 */

/**
 * Menu
 */
export default class Menu extends Base {
  /**
   * Configuration
   */
  static config = {
    name: 'Menu',
    components: {
      MenuItem,
      SubMenuItem,
    },
    refs: ['openMenu', 'media', 'menu'],
  };

  /**
   * @member {boolean} isOpen
   */
  isOpen = false;

  /**
   *
   */
  loaded() {
    this.setMenuSize();
  }

  /**
   * Resized hook
   */
  resized() {
    this.setMenuSize();
  }

  /**
   * Set menu sizes
   * @description Set full Menu size for submenus so even
   * when scrolling in the menu submenu is still fullscreen
   */
  setMenuSize() {
    // reset current height
    this.$children.MenuItem.forEach((item) => {
      if (!item.$options.hasSubmenu) {
        return;
      }

      item.$refs.submenu.style.height = `0px`;
    });
    this.$children.SubMenuItem.forEach((item) => {
      item.$refs.submenu.style.height = `0px`;
    });

    const windowHeight = window.innerHeight;
    const height = this.$refs.menu.scrollHeight;

    this.$children.MenuItem.forEach((item) => {
      if (!item.$options.hasSubmenu) {
        return;
      }

      item.$refs.submenu.style.height = `${Math.max(height, windowHeight)}px`;
    });
    this.$children.SubMenuItem.forEach((item) => {
      item.$refs.submenu.style.height = `${Math.max(height, windowHeight)}px`;
    });
  }

  /**
   * Burger menu
   * @returns {Function}
   */
  onOpenMenuClick() {
    if (this.isOpen) {
      return this.close();
    }

    return this.open();
  }

  /**
   * onMenuItemClick
   * @description Close the SubMenuItem & MenuItem that are open when opening a MenuItem
   * to avoid having a SubMenuItem open while opening a MenuItem
   * this is only triggered for touchDevice, and only usefull on landscape tablet device
   */
  onMenuItemClick() {
    this.$children.SubMenuItem.forEach((item) => {
      item.close();
    });
    this.$children.MenuItem.forEach((item) => {
      item.close();
    });
  }

  /**
   * Update the Media
   * @param {string} src source.
   * @param {string} type HTML type.
   */
  onMenuItemMediaUpdate(src, type) {
    const mediaElement = document.createElement(type);
    mediaElement.src = src;
    mediaElement.style.opacity = 0;

    const event = type === 'video' ? 'loadeddata' : 'load';

    if (type === 'video') {
      mediaElement.autoplay = true;
      mediaElement.controls = false;
      mediaElement.loop = true;
      mediaElement.playsInline = true;
    }

    mediaElement.addEventListener(event, async () => {
      await this.$refs.media.append(mediaElement);

      await transition(mediaElement, {
        from: { opacity: 0 },
        active: { transition: '0.6s opacity ease-out' },
        to: { opacity: 1 },
      });

      nextFrame(() => {
        this.$refs.media.firstElementChild.remove();
      });
    });
  }

  /**
   * open
   */
  open() {
    this.isOpen = true;
    document.body.classList.add('is-menu-open');
    document.body.classList.add('overflow-hidden');
  }

  /**
   * close
   */
  close() {
    this.isOpen = false;
    document.body.classList.remove('is-menu-open');
    document.body.classList.remove('overflow-hidden');

    this.$children.MenuItem.forEach((item) => {
      item.close();
    });

    this.$children.SubMenuItem.forEach((item) => {
      item.close();
    });
  }

  /**
   * Keyed service hook
   * @see https://js-toolkit.studiometa.dev/api/methods-hooks-services.html#keyed
   * @param {object} props The keyed serivce props.
   * @param {boolean} props.ESC Will be true if the key initiating the event is the esc key.
   */
  keyed({ ESC }) {
    if (ESC) {
      this.close();
    }
  }
}
